import Image, { ImageProps } from '@/components/Image';
import Video from '@/components/Video';
import YouTubeVideo from '@/components/YouTubeVideo';
import { COLORS } from '@/styles/color';
import { marginY, mediaBreakpointUp, paddingX, paddingY } from '@/styles/layout/utils';
import { FONT_FAMILIES, FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import { FC } from 'react';
import styled from 'styled-components';

interface ParagraphProps {
  textAlign?: VizyTextAlign;
}

const Paragraph = styled.p<ParagraphProps>`
  ${customTextSize(16, 24)};
  text-align: ${(props) => props.textAlign ?? 'left'};
  margin-bottom: ${remCalc(32)};
  white-space: pre-line;

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(18, 26)};
  }

  li > & {
    margin-bottom: ${remCalc(16)};
  }
`;

const Highlight = styled.span`
  background-color: var(--article-accent-color-highlight);
`;

const TextLink = styled.a`
  color: ${COLORS.BLUE.LOWLIGHT};

  &:hover {
    color: ${COLORS.BLACK};
  }
`;

const Strikethrough = styled.span`
  text-decoration: line-through;
`;

const InlineCode = styled.code`
  ${customTextSize(16, 24)};
  ${paddingX(5)}
  background-color: ${COLORS.GRAY.HIGHLIGHT};
  border-radius: ${remCalc(5)};
`;

const CodeBlock = styled.pre`
  ${customTextSize(16, 24)};
  ${paddingX(20)}
  ${paddingY(15)}
  display: block;
  white-space: pre-wrap;
  word-break: break-word;
  background-color: ${COLORS.BLACK};
  color: ${COLORS.WHITE};
  border-radius: ${remCalc(5)};
  margin-bottom: ${remCalc(32)};
`;

const Heading1 = styled.h1<ParagraphProps>`
  text-align: ${(props) => props.textAlign};
  ${customTextSize(48, 54)};
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin-bottom: ${remCalc(26)};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(72, 78)};
    margin-bottom: ${remCalc(40)};
  }
`;

const Heading2 = styled.h2<ParagraphProps>`
  text-align: ${(props) => props.textAlign};
  ${customTextSize(32, 38)};
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin-bottom: ${remCalc(26)};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(49, 58)};
    margin-bottom: ${remCalc(40)};
  }
`;

const Heading3 = styled.h3<ParagraphProps>`
  text-align: ${(props) => props.textAlign};
  ${customTextSize(24, 30)};
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(32, 38)};
    margin-bottom: ${remCalc(32)};
  }
`;

const Heading4 = styled.h4<ParagraphProps>`
  text-align: ${(props) => props.textAlign};
  ${customTextSize(16, 21)};
  font-weight: ${FONT_WEIGHTS.BOLD};
  margin-bottom: ${remCalc(22)};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(24, 24)};
    margin-bottom: ${remCalc(32)};
  }
`;

const Heading5 = styled.h5<ParagraphProps>`
  text-align: ${(props) => props.textAlign};
  // TODO
`;

const Heading6 = styled.h6<ParagraphProps>`
  text-align: ${(props) => props.textAlign};
  // TODO
`;

const ListItem = styled.li``;

const UnorderedList = styled.ul`
  margin-bottom: ${remCalc(32)};

  ${ListItem} {
    position: relative;
    padding-left: ${remCalc(32)};

    &::before {
      content: '';
      width: ${remCalc(6)};
      height: ${remCalc(6)};
      background: currentcolor;
      border-radius: 50%;
      position: absolute;
      top: ${remCalc(10)};
      left: 0;
    }
  }
`;

const OrderedList = styled.ol`
  counter-reset: olCounter ${(props) => Math.max((props.start ?? 0) - 1, 0)};
  margin-bottom: ${remCalc(32)};

  ${ListItem} {
    counter-increment: olCounter 1;
    position: relative;
    padding-left: ${remCalc(32)};

    &::before {
      content: counter(olCounter);
      width: ${remCalc(20)};
      height: ${remCalc(20)};
      position: absolute;
      top: ${remCalc(3)};
      left: 0;
      background-color: ${COLORS.BLACK};
      border-radius: 100%;
      color: ${COLORS.WHITE};
      ${customTextSize(12, 0)}
      padding-bottom: 2px;
      padding-right: 1px;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;

const HorizontalRule = styled.hr`
  ${marginY(32)};
`;

const Blockquote = styled.figure`
  ${marginY(48)}

  blockquote {
    position: relative;
    ${customTextSize(28, 36)};
    font-family: ${FONT_FAMILIES.SERIF};
    font-weight: ${FONT_WEIGHTS.MEDIUM};

    ${mediaBreakpointUp('desktop')} {
      ${customTextSize(40, 52)};
    }

    &::before {
      content: '“';
      position: absolute;
      left: ${remCalc(-16)};
      top: 0;

      ${mediaBreakpointUp('desktop')} {
        left: ${remCalc(-22)};
      }
    }

    &::after {
      content: '”';
      margin-left: ${remCalc(8)};
    }

    p {
      display: inline;
    }
  }

  figcaption {
    margin-top: ${remCalc(24)};
  }
`;

const Figure = styled.figure`
  ${marginY(48)}

  figcaption {
    ${customTextSize(16, 24)};
    font-style: italic;
    margin-top: ${remCalc(8)};
  }
`;

const StyledYouTubeVideo = styled(YouTubeVideo)`
  ${marginY(48)}
`;

const StyledVideo = styled(Video)`
  ${marginY(48)}
`;

type StyledImageProps = ImageProps & {
  rounded: boolean;
};

const StyledImage = styled<FC<StyledImageProps>>(Image)`
  border-radius: ${(props) => (props.rounded ? remCalc(10) : 0)};
  overflow: hidden;
`;

const vizyTextPageComponents = {
  Paragraph,
  Highlight,
  TextLink,
  Strikethrough,
  InlineCode,
  CodeBlock,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  ListItem,
  UnorderedList,
  OrderedList,
  HorizontalRule,
  Blockquote,
  Figure,
  YouTubeVideo: StyledYouTubeVideo,
  Video: StyledVideo,
  Image: StyledImage,
};

export default vizyTextPageComponents;
